/**
 * Marketplace Order Detail Page
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import {
  useGetMarketplaceOrderQuery,
  useUpdateMarketplaceOrderStatusMutation,
} from '../../store/api';
import { MarketplaceOrderStatus } from '../../types/marketplace';
import { formatCurrency } from '../../utils/pricing';
import { formatNumber } from '../../utils/format';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const ORDER_STATUS_COLOR: Record<
  MarketplaceOrderStatus,
  'default' | 'info' | 'primary' | 'success' | 'error' | 'warning' | 'secondary'
> = {
  PENDING: 'warning',
  CONFIRMED: 'info',
  PROCESSING: 'primary',
  OUT_FOR_DELIVERY: 'secondary',
  DELIVERED: 'success',
  CANCELLED: 'error',
  RETURNED: 'default',
  REFUNDED: 'default',
};

const STATUS_OPTIONS = Object.keys(ORDER_STATUS_COLOR) as MarketplaceOrderStatus[];

// Go back to the shared Orders page with the Marketplace tab preselected, rather
// than the standalone list, so the Services/Marketplace switcher stays available.
const MARKETPLACE_ORDERS_PATH = '/orders?tab=marketplace';

const PAYMENT_STATUS_COLOR: Record<
  string,
  'default' | 'info' | 'primary' | 'success' | 'error' | 'warning'
> = {
  PENDING: 'warning',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
  REFUNDED: 'default',
  PARTIALLY_REFUNDED: 'default',
};

export const MarketplaceOrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data, isLoading, error } = useGetMarketplaceOrderQuery(Number(id));
  const [updateStatus, { isLoading: isUpdating }] = useUpdateMarketplaceOrderStatusMutation();

  const order = data?.data;

  const [newStatus, setNewStatus] = useState<MarketplaceOrderStatus | ''>('');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    if (order) {
      setNewStatus(order.status);
      setAdminNotes(order.admin_notes || '');
    }
  }, [order]);

  const handleStatusUpdate = async () => {
    if (!order || !newStatus) return;
    try {
      await updateStatus({
        id: order.id,
        status: newStatus,
        admin_notes: adminNotes || undefined,
      }).unwrap();
      toast.success(t('orders.detail.statusUpdated'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('orders.detail.statusUpdateFailed'));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error">{t('orders.detail.notFound')}</Typography>
        <Button onClick={() => navigate(MARKETPLACE_ORDERS_PATH)} sx={{ mt: 2 }}>
          {t('orders.detail.backToOrders')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('orders.detail.heading', { number: order.order_number })}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.marketplaceOrders'), path: MARKETPLACE_ORDERS_PATH },
          { label: order.order_number },
        ]}
      />

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(MARKETPLACE_ORDERS_PATH)} sx={{ mb: 3 }}>
        {t('orders.detail.backToOrders')}
      </Button>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Main Content */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>{t('orders.detail.orderDetails')}</Typography>
                <Chip
                  label={t(`orders.status.${order.status}`, { defaultValue: order.status })}
                  color={ORDER_STATUS_COLOR[order.status] ?? 'default'}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('orders.detail.orderNumber')}</Typography>
                  <Typography variant="body1">{order.order_number}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('orders.detail.customer')}</Typography>
                  <Typography variant="body1">{order.user_email}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('orders.detail.contactPhone')}</Typography>
                  <Typography variant="body1">{order.contact_phone || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('orders.detail.created')}</Typography>
                  <Typography variant="body1">{moment(order.created_at).format('MMMM DD, YYYY HH:mm')}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>{t('orders.detail.deliveryAddress')}</Typography>
              <Typography variant="body1">{order.full_address || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {[order.city, order.area].filter(Boolean).join(', ') || '—'}
              </Typography>

              {order.notes && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>{t('orders.detail.customerNotes')}</Typography>
                  <Typography variant="body1">{order.notes}</Typography>
                </>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>{t('orders.detail.items')}</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('orders.detail.product')}</TableCell>
                      <TableCell>{t('orders.detail.type')}</TableCell>
                      <TableCell align="center">{t('orders.detail.qty')}</TableCell>
                      <TableCell align="right">{t('orders.detail.unitPrice')}</TableCell>
                      <TableCell>{t('orders.detail.rental')}</TableCell>
                      <TableCell align="right">{t('orders.detail.insurance')}</TableCell>
                      <TableCell align="right">{t('orders.detail.lineTotal')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.purchase_type === 'RENT' ? t('orders.detail.rent') : t('orders.detail.buy')}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">{formatNumber(item.quantity)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell>
                          {item.purchase_type === 'RENT' && item.rental_start_date ? (
                            <Box>
                              <Typography variant="caption" display="block">
                                {moment(item.rental_start_date).format('MMM DD')} -{' '}
                                {item.rental_end_date ? moment(item.rental_end_date).format('MMM DD') : '—'}
                              </Typography>
                              {item.rental_days != null && (
                                <Typography variant="caption" color="text.secondary">
                                  {t('orders.detail.rentalDays', { count: item.rental_days })}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {item.insurance_selected ? formatCurrency(item.insurance_price) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(item.line_total)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box>
          {/* Totals */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>{t('orders.detail.totals')}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('orders.detail.subtotal')}</Typography>
                  <Typography variant="body2">{formatCurrency(order.subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('orders.detail.insuranceTotal')}</Typography>
                  <Typography variant="body2">{formatCurrency(order.insurance_total)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('orders.detail.deliveryFee')}</Typography>
                  <Typography variant="body2">{formatCurrency(order.delivery_fee)}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight={600}>{t('orders.detail.total')}</Typography>
                  <Typography variant="body1" fontWeight={600} color="primary">
                    {formatCurrency(order.total_amount)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Payment */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>{t('orders.detail.payment')}</Typography>
              {order.payment ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('orders.detail.paymentStatus')}</Typography>
                    <Chip
                      label={t(`payments.status.${order.payment.status}`, {
                        defaultValue: order.payment.status,
                      })}
                      size="small"
                      color={PAYMENT_STATUS_COLOR[order.payment.status] ?? 'default'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('orders.detail.paymentMethod')}</Typography>
                    <Typography variant="body2">
                      {order.payment.payment_method === 'CASH'
                        ? t('orders.detail.cashOnDelivery')
                        : t('orders.detail.fawry')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('orders.detail.amount')}</Typography>
                    <Typography variant="body2">{formatCurrency(order.payment.amount)}</Typography>
                  </Box>
                  {order.payment.paid_at && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('orders.detail.paidAt')}</Typography>
                      <Typography variant="body2">
                        {moment(order.payment.paid_at).format('MMM DD, YYYY HH:mm')}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('orders.detail.reference')}</Typography>
                    <Typography variant="body2">{order.payment.payment_number}</Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('orders.detail.noPayment')}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Status Update */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>{t('orders.detail.updateStatus')}</Typography>
              <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                <InputLabel>{t('orders.detail.status')}</InputLabel>
                <Select
                  value={newStatus}
                  label={t('orders.detail.status')}
                  onChange={(e) => setNewStatus(e.target.value as MarketplaceOrderStatus)}
                >
                  {STATUS_OPTIONS.map((key) => (
                    <MenuItem key={key} value={key}>
                      {t(`orders.status.${key}`, { defaultValue: key })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('orders.detail.adminNotes')}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleStatusUpdate}
                disabled={isUpdating || !newStatus}
              >
                {isUpdating ? t('orders.detail.saving') : t('orders.detail.save')}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default MarketplaceOrderDetailPage;
